import React, { useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { NwRemoveButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { ARTICLE_BLOCK_TYPE, ARTICLE_BLOCK_TYPE_OPTIONS } from '@containers/Art/Articles/Articles.constants';

import {
    ARTICLE_TEXT_FORMATS,
    ARTICLE_TEXT_MODULES,
    ARTICLE_TITLE_DEFAULT_VALUE,
    ARTICLE_TITLE_FORMATS,
    ARTICLE_TITLE_MODULES,
} from './articleEditorDrawerConstants';
import ArticleEditorFileBlock from './ArticleEditorFileBlock';
import ArticleEditorMediaBlock from './ArticleEditorMediaBlock';
import {
    ArticleBlockActions,
    ArticleBlockBody,
    ArticleBlockDragHandle,
    ArticleBlockHeader,
    ArticleBlockTitleGroup,
    ArticleBlockRoot,
    ArticleBlockTypeLabel,
    QuillEditorWrap,
} from './ArticleEditorBlock.styles';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ArticleEditorBlock = ({
    block,
    column,
    dragHandleProps = {},
    dragRef,
    draggableProps = {},
    draggableStyle,
    isDragging,
    row,
    onApplyEmbedMedia,
    onChangeBlockText,
    onChangeBlockTitle,
    onChangeEmbedInput,
    onChangeLinkText,
    onChangeMediaSource,
    onChangeNetwalkModel,
    onOpenMediaSelector,
    onRemoveBlock,
    onSelectUploadedMedia,
    onUploadFileEnd,
    onUploadMediaEnd,
    uploadedSelectorZIndex,
}) => {
    const titleEditorRef = useRef(null);

    const applyDefaultTitleHeading = () => {
        const editor = titleEditorRef.current?.getEditor?.();
        if (!editor || editor.getLength() > 1) return;

        if (!editor.getSelection()) {
            editor.setSelection(0, 0, 'silent');
        }
        editor.format('header', 1, 'silent');
    };

    const handleChangeTitleEditor = (title, delta, source, editor) => {
        // Ignore programmatic changes (e.g. on mount); only react to actual user edits.
        if (source !== 'user') return;
        const plainTitle = editor.getText().trim();
        onChangeBlockTitle(row.ID, column.ID, block.ID, plainTitle ? title : '');
    };

    let bodyContent;
    if (block.Type === ARTICLE_BLOCK_TYPE.TITLE) {
        bodyContent = (
            <QuillEditorWrap $isTitleEditor>
                <ReactQuill
                    formats={ARTICLE_TITLE_FORMATS}
                    modules={ARTICLE_TITLE_MODULES}
                    placeholder="Title"
                    ref={titleEditorRef}
                    theme="snow"
                    value={block.Title || ARTICLE_TITLE_DEFAULT_VALUE}
                    onChange={handleChangeTitleEditor}
                    onFocus={applyDefaultTitleHeading}
                />
            </QuillEditorWrap>
        );
    } else if (block.Type === ARTICLE_BLOCK_TYPE.MEDIA) {
        bodyContent = (
            <ArticleEditorMediaBlock
                block={block}
                column={column}
                row={row}
                onApplyEmbedMedia={onApplyEmbedMedia}
                onChangeEmbedInput={onChangeEmbedInput}
                onChangeMediaSource={onChangeMediaSource}
                onChangeNetwalkModel={onChangeNetwalkModel}
                onOpenMediaSelector={onOpenMediaSelector}
                onSelectUploadedMedia={onSelectUploadedMedia}
                onUploadMediaEnd={onUploadMediaEnd}
                uploadedSelectorZIndex={uploadedSelectorZIndex}
            />
        );
    } else if (block.Type === ARTICLE_BLOCK_TYPE.FILE) {
        bodyContent = (
            <ArticleEditorFileBlock
                block={block}
                column={column}
                row={row}
                onChangeLinkText={onChangeLinkText}
                onUploadFileEnd={onUploadFileEnd}
            />
        );
    } else {
        bodyContent = (
            <QuillEditorWrap>
                <ReactQuill
                    formats={ARTICLE_TEXT_FORMATS}
                    modules={ARTICLE_TEXT_MODULES}
                    placeholder="Write text"
                    theme="snow"
                    value={block.Text || ''}
                    onChange={(text, delta, source) => {
                        // Ignore programmatic changes (e.g. on mount); only react to actual user edits.
                        if (source !== 'user') return;
                        onChangeBlockText(row.ID, column.ID, block.ID, text);
                    }}
                />
            </QuillEditorWrap>
        );
    }

    const blockTypeLabel = ARTICLE_BLOCK_TYPE_OPTIONS.find((o) => o.value === block.Type)?.label || block.Type;

    return (
        <ArticleBlockRoot
            key={block.ID}
            {...draggableProps}
            className={isDragging ? 'is-dragging' : ''}
            data-article-editor-block-id={block.ID}
            ref={dragRef}
            style={draggableStyle}
        >
            <ArticleBlockHeader>
                <ArticleBlockTitleGroup>
                    <ArticleBlockDragHandle
                        {...dragHandleProps}
                        aria-label="Drag block"
                        title="Drag block"
                        type="button"
                    >
                        <NwIcon icon={byPrefixAndName.fasl['sort']} />
                    </ArticleBlockDragHandle>
                    <ArticleBlockTypeLabel>{blockTypeLabel}</ArticleBlockTypeLabel>
                </ArticleBlockTitleGroup>
                <ArticleBlockActions>
                    <NwRemoveButton
                        danger
                        ghost
                        icon={byPrefixAndName.fass['circle-xmark']}
                        iconOnly
                        needConfirmation={false}
                        tooltip="Remove block"
                        onClick={() => onRemoveBlock(row.ID, column.ID, block.ID)}
                    />
                </ArticleBlockActions>
            </ArticleBlockHeader>
            <ArticleBlockBody>{bodyContent}</ArticleBlockBody>
        </ArticleBlockRoot>
    );
};

export default ArticleEditorBlock;
